import { useState } from 'react';
import Sidebar from '../dashboard/components/Sidebar';

const FollowUps = () => {
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [newFollowUp, setNewFollowUp] = useState({
    client: '',
    type: 'phone',
    date: '',
    time: '',
    priority: 'medium',
    project: '',
    notes: ''
  });
  const [exportOptions, setExportOptions] = useState({
    format: 'csv',
    dateRange: 'all',
    customStartDate: '',
    customEndDate: '',
    status: 'all',
    includeNotes: true,
    includeProjects: true
  });

  const handleAddFollowUp = () => {
    setShowAddModal(true);
  };

  const handleSaveFollowUp = () => {
    if (newFollowUp.client && newFollowUp.date && newFollowUp.time) {
      // Here you would typically save to a database
      console.log('Saving follow-up:', newFollowUp);
      setShowAddModal(false);
      setNewFollowUp({
        client: '',
        type: 'call',
        date: '',
        time: '',
        priority: 'medium',
        notes: '',
        project: ''
      });
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewFollowUp({
      client: '',
      type: 'call',
      date: '',
      time: '',
      priority: 'medium',
      notes: '',
      project: ''
    });
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportData = () => {
    // Filter follow-ups based on export options
    let filteredFollowUps = [...followUps];

    // Filter by date range
    if (exportOptions.dateRange !== 'all') {
      const now = new Date();
      let startDate = new Date();
      
      if (exportOptions.dateRange === '30days') {
        startDate.setDate(now.getDate() - 30);
      } else if (exportOptions.dateRange === '90days') {
        startDate.setDate(now.getDate() - 90);
      } else if (exportOptions.dateRange === 'custom') {
        startDate = new Date(exportOptions.customStartDate);
        const endDate = new Date(exportOptions.customEndDate);
        filteredFollowUps = filteredFollowUps.filter(followUp => {
          const followUpDate = new Date(followUp.date);
          return followUpDate >= startDate && followUpDate <= endDate;
        });
      }

      if (exportOptions.dateRange !== 'custom') {
        filteredFollowUps = filteredFollowUps.filter(followUp => {
          const followUpDate = new Date(followUp.date);
          return followUpDate >= startDate;
        });
      }
    }

    // Filter by status
    if (exportOptions.status !== 'all') {
      filteredFollowUps = filteredFollowUps.filter(followUp => followUp.status === exportOptions.status);
    }

    // Generate export data
    if (exportOptions.format === 'csv') {
      const headers = ['Client', 'Type', 'Date', 'Time', 'Status', 'Priority'];
      if (exportOptions.includeProjects) headers.push('Project');
      if (exportOptions.includeNotes) headers.push('Notes');

      const csvContent = [
        headers.join(','),
        ...filteredFollowUps.map(followUp => {
          const row = [
            `"${followUp.client}"`,
            `"${followUp.type}"`,
            `"${followUp.date}"`,
            `"${followUp.time}"`,
            `"${followUp.status}"`,
            `"${followUp.priority}"`
          ];
          if (exportOptions.includeProjects) row.push(`"${followUp.project || 'N/A'}"`);
          if (exportOptions.includeNotes) row.push(`"${followUp.notes || ''}"`);
          return row.join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `followups_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      const jsonData = filteredFollowUps.map(followUp => {
        const data: any = {
          client: followUp.client,
          type: followUp.type,
          date: followUp.date,
          time: followUp.time,
          status: followUp.status,
          priority: followUp.priority
        };
        if (exportOptions.includeProjects) data.project = followUp.project || null;
        if (exportOptions.includeNotes) data.notes = followUp.notes || '';
        return data;
      });

      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `followups_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    }

    setShowExportModal(false);
  };

  const handleCloseExportModal = () => {
    setShowExportModal(false);
    setExportOptions({
      format: 'csv',
      dateRange: 'all',
      customStartDate: '',
      customEndDate: '',
      status: 'all',
      includeNotes: true,
      includeProjects: true
    });
  };

  const followUps = [
    {
      id: 1,
      client: 'Sarah Johnson',
      company: 'Tech Solutions Inc.',
      type: 'call',
      date: '2024-01-25',
      time: '10:00 AM',
      priority: 'high',
      status: 'pending',
      notes: 'Discuss project timeline and budget adjustments',
      project: 'Office Renovation'
    },
    {
      id: 2,
      client: 'Michael Chen',
      company: 'Creative Studio',
      type: 'email',
      date: '2024-01-26',
      time: '2:00 PM',
      priority: 'medium',
      status: 'pending',
      notes: 'Send updated design proposals and material samples',
      project: 'Retail Space Design'
    },
    {
      id: 3,
      client: 'Emily Rodriguez',
      company: 'Global Industries',
      type: 'meeting',
      date: '2024-01-24',
      time: '11:30 AM',
      priority: 'high',
      status: 'completed',
      notes: 'Final walkthrough and project sign-off',
      project: 'Warehouse Expansion'
    },
    {
      id: 4,
      client: 'David Thompson',
      company: 'Local Restaurant',
      type: 'call',
      date: '2024-01-27',
      time: '9:00 AM',
      priority: 'low',
      status: 'pending',
      notes: 'Check on permit status and schedule inspection',
      project: 'Kitchen Remodel'
    },
    {
      id: 5,
      client: 'Lisa Wang',
      company: 'Startup Hub',
      type: 'email',
      date: '2024-01-23',
      time: '3:30 PM',
      priority: 'medium',
      status: 'overdue',
      notes: 'Follow up on contract terms and payment schedule',
      project: 'Co-working Space'
    }
  ];

  const getFilteredFollowUps = () => {
    if (filter === 'all') return followUps;
    return followUps.filter(followUp => followUp.status === filter);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'call': return 'ri-phone-line';
      case 'email': return 'ri-mail-line';
      case 'meeting': return 'ri-calendar-event-line';
      default: return 'ri-notification-line';
    }
  };

  const filteredFollowUps = getFilteredFollowUps();

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Follow Ups</h1>
              <p className="text-sm text-gray-600 mt-1">Track and manage client follow-up tasks</p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleExport}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center whitespace-nowrap"
              >
                <i className="ri-download-line mr-2"></i>
                Export
              </button>
              <button 
                onClick={handleAddFollowUp}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center whitespace-nowrap"
              >
                <i className="ri-add-line mr-2"></i>
                Add Follow Up
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {/* Filter Tabs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { key: 'all', label: 'All Follow Ups', count: followUps.length },
                  { key: 'pending', label: 'Pending', count: followUps.filter(f => f.status === 'pending').length },
                  { key: 'overdue', label: 'Overdue', count: followUps.filter(f => f.status === 'overdue').length },
                  { key: 'completed', label: 'Completed', count: followUps.filter(f => f.status === 'completed').length }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      filter === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </nav>
            </div>

            {/* Follow Ups List */}
            <div className="p-6">
              {filteredFollowUps.length > 0 ? (
                <div className="space-y-4">
                  {filteredFollowUps.map((followUp) => (
                    <div key={followUp.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className={`${getTypeIcon(followUp.type)} text-blue-600`}></i>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">{followUp.client}</h3>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(followUp.status)}`}>
                                {followUp.status}
                              </span>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(followUp.priority)}`}>
                                {followUp.priority} priority
                              </span>
                            </div>
                            
                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center">
                                <i className="ri-building-line mr-2"></i>
                                {followUp.company}
                              </div>
                              <div className="flex items-center">
                                <i className="ri-calendar-line mr-2"></i>
                                {followUp.date} at {followUp.time}
                              </div>
                              <div className="flex items-center">
                                <i className="ri-folder-line mr-2"></i>
                                {followUp.project}
                              </div>
                              {followUp.notes && (
                                <div className="flex items-start mt-2">
                                  <i className="ri-file-text-line mr-2 mt-0.5"></i>
                                  <span className="text-gray-700">{followUp.notes}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <i className="ri-edit-line"></i>
                          </button>
                          <button className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg">
                            <i className="ri-check-line"></i>
                          </button>
                          <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <i className="ri-calendar-check-line text-4xl text-gray-300 mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No follow-ups found</h3>
                  <p className="text-gray-500 mb-4">
                    {filter === 'all' 
                      ? "You don't have any follow-ups scheduled yet."
                      : `No ${filter} follow-ups at the moment.`
                    }
                  </p>
                  <button 
                    onClick={handleAddFollowUp}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 whitespace-nowrap"
                  >
                    Add Your First Follow Up
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Follow Up Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Follow Up</h3>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    value={newFollowUp.client}
                    onChange={(e) => setNewFollowUp(prev => ({ ...prev, client: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter client name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Follow Up Type
                  </label>
                  <select
                    value={newFollowUp.type}
                    onChange={(e) => setNewFollowUp(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="call">Phone Call</option>
                    <option value="email">Email</option>
                    <option value="meeting">Meeting</option>
                    <option value="site_visit">Site Visit</option>
                    <option value="proposal">Send Proposal</option>
                    <option value="contract">Contract Review</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={newFollowUp.date}
                    onChange={(e) => setNewFollowUp(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time *
                  </label>
                  <input
                    type="time"
                    value={newFollowUp.time}
                    onChange={(e) => setNewFollowUp(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority Level
                  </label>
                  <select
                    value={newFollowUp.priority}
                    onChange={(e) => setNewFollowUp(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Related Project
                  </label>
                  <select
                    value={newFollowUp.project}
                    onChange={(e) => setNewFollowUp(prev => ({ ...prev, project: e.target.value }))}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select project</option>
                    <option value="Office Renovation">Office Renovation</option>
                    <option value="Retail Space Design">Retail Space Design</option>
                    <option value="Warehouse Expansion">Warehouse Expansion</option>
                    <option value="Kitchen Remodel">Kitchen Remodel</option>
                    <option value="Co-working Space">Co-working Space</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={newFollowUp.notes}
                  onChange={(e) => setNewFollowUp(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any additional notes or details about this follow-up..."
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFollowUp}
                disabled={!newFollowUp.client || !newFollowUp.date || !newFollowUp.time}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Create Follow Up
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Export Follow Ups</h3>
              <button
                onClick={handleCloseExportModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="csv"
                      checked={exportOptions.format === 'csv'}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">CSV (Spreadsheet)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="json"
                      checked={exportOptions.format === 'json'}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">JSON (Data)</span>
                  </label>
                </div>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <select
                  value={exportOptions.dateRange}
                  onChange={(e) => setExportOptions(prev => ({ ...prev, dateRange: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                >
                  <option value="all">All Time</option>
                  <option value="30days">Last 30 Days</option>
                  <option value="90days">Last 90 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Custom Date Range */}
              {exportOptions.dateRange === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={exportOptions.customStartDate}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, customStartDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={exportOptions.customEndDate}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, customEndDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Status
                </label>
                <select
                  value={exportOptions.status}
                  onChange={(e) => setExportOptions(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Include Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Include in Export
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeProjects}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeProjects: e.target.checked }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Project Information</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeNotes}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeNotes: e.target.checked }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Notes</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseExportModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleExportData}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 whitespace-nowrap"
              >
                Export Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUps;
